import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    OnDestroy,
    OnInit,
    ViewChild,
    ViewEncapsulation
} from "@angular/core";
import {Api, ApiService} from "../../../../../../../common/services/api.service";
import {ToastService} from "../../../../../../../common/services/toast.service";
import {UserService} from "../../../../../../../common/services/user.service";
import {Base} from "../../../../../../../common/interfaces/base.interfaces";
import {SpinnerService} from "../../../../../../../common/services/spinner.service";
import {Order} from "../../../../../../../common/interfaces/order.interface";
import {CdkDragDrop, moveItemInArray} from "@angular/cdk/drag-drop";
import {ModalService} from "../../../../../../services/modal.service";
import {RemarksClassificationsFormComponent} from "../form/form.component";
import {ConfirmComponent} from "../../../../../../../common/components/confirm/confirm.component";
import {Api3Service} from "../../../../../../../common/services/api3.service";
import IRemarkClassification = Order.IRemarkClassification;
import {PartnerService} from "../../../../../../../common/services/partner.service";

interface IRemarkClassificationExt extends IRemarkClassification {
    can_edit: boolean;
    can_delete: boolean;
}

@Component({
    selector: "section-remarks-classifications-list",
    templateUrl: "list.component.html",
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    styleUrls: ["list.component.scss"]
})

export class RemarksClassificationsListComponent implements Base.IComponent, OnDestroy, OnInit {

    private destroy$: EventEmitter<boolean> = new EventEmitter(false);

    public readonly state: Base.IState;

    public remarkClassifications: IRemarkClassificationExt[] = null;

    @ViewChild(ConfirmComponent, {static: true})
    public confirm: ConfirmComponent;

    public constructor(
        private apiService: ApiService,
        private api3Service: Api3Service,
        private toastService: ToastService,
        public userService: UserService,
        private spinnerService: SpinnerService,
        private changeDetectorRef: ChangeDetectorRef,
        protected modalService: ModalService
    ) {
    }

    public async prepareData(page: number = 1, per_page: number = 25): Promise<any> {
        this.remarkClassifications = null;
        this.changeDetectorRef.markForCheck();

        const {data}: Api.IResponse = await this.api3Service.get(`${this.state.section}/remarks/classifications`);

        if (data) {
            this.remarkClassifications = data;

            const partner_id = PartnerService.partner.id;

            const canEdit: boolean = this.userService.validatePermissions("edit_order_remark_classifications");
            const canDelete: boolean = this.userService.validatePermissions("delete_order_remark_classifications");

            for (const c of this.remarkClassifications) {
                c.can_edit = canEdit;
                c.can_delete = c.partner_id === partner_id && canDelete;
            }
            this.changeDetectorRef.markForCheck();
        }
    }

    public drop(event: CdkDragDrop<string[]>): void {
        moveItemInArray(this.remarkClassifications, event.previousIndex, event.currentIndex);
        this.updatePriority();
    }

    /**
     * Create remark classification
     * @returns {Promise<any>}
     */
    public async updatePriority(): Promise<any> {
        this.spinnerService.show();
        const {code, message}: Api.IResponse = await this.api3Service.post(
            `${this.state.section}/remarks/classifications/update-priority`, {
                priorities: this.remarkClassifications.map((rc: IRemarkClassification, index: number): any => {
                    return {
                        id: rc.id,
                        priority: index + 1
                    };
                })
            });

        this.spinnerService.hide();

        if (code === 200) {
            this.toastService.show(message, "success");
        }
    }

    public async edit(data: IRemarkClassification): Promise<any> {
        await this.modalService.open(RemarksClassificationsFormComponent, {data, state: this.state});
        this.prepareData();
    }

    public async delete(data: IRemarkClassification): Promise<any> {
        if (!await this.confirm.confirm("This action cannot be reversed!")) {
            return;
        }
        this.spinnerService.show();
        const {code, message}: Api.IResponse = await this.api3Service.delete(Api.EMethod.Delete,
            `${this.state.section}/remarks/classifications/${data.id}`);
        this.spinnerService.hide();

        if (code === 200) {
            this.toastService.show(message, "success");
            this.prepareData();
        }
    }

    public async add(): Promise<any> {
        await this.modalService.open(RemarksClassificationsFormComponent, {state: this.state});
        this.prepareData();
    }

    public ngOnInit(): void {
        this.prepareData();
    }

    public ngOnDestroy(): void {
        this.destroy$.next(true);
        this.destroy$.unsubscribe();
    }

    public ngConfig(): Base.IConfig {
        return {
            name: "remarks-classifications",
            actions: {
                "browse": ["browse_order_remark_classifications"]
            }
        };
    }
}
